import { z } from 'zod';
import { PROJECT_ROLES } from './roles.js';
import type { FlowGraph, Json } from './types.js';
import { HTTP_METHODS, TOKEN_SCOPES } from './types.js';

export const zHttpMethod = z.enum(HTTP_METHODS);
export const zProjectRole = z.enum(PROJECT_ROLES);
export const zTokenScope = z.enum(TOKEN_SCOPES);

export const zJson: z.ZodType<Json> = z.lazy(() =>
  z.union([z.string(), z.number(), z.boolean(), z.null(), z.array(zJson), z.record(z.string(), zJson)]),
) as z.ZodType<Json>;

export const zFieldSpec = z.object({
  name: z.string().min(1).max(120),
  type: z.enum(['string', 'number', 'boolean', 'object', 'array', 'any']),
  required: z.boolean().optional(),
  default: zJson.optional(),
  description: z.string().max(500).optional(),
});

const strMap = z.record(z.string(), z.string());

/* ------------------------------- node data ------------------------------- */

const zTriggerHttp = z.object({
  method: zHttpMethod,
  path: z
    .string()
    .min(1)
    .max(300)
    .regex(/^\/[A-Za-z0-9\-._~/:]*$/, 'Path must start with / and use url-safe characters'),
  authMode: z.enum(['public', 'token', 'session', 'either']),
  minRole: zProjectRole.optional(),
  scopes: z.array(zTokenScope).optional(),
  query: z.array(zFieldSpec).optional(),
  body: z.array(zFieldSpec).optional(),
  description: z.string().max(1000).optional(),
});

const zAuthGuard = z.object({
  minRole: zProjectRole.optional(),
  scopes: z.array(zTokenScope).optional(),
  expression: z.string().max(2000).optional(),
  denyStatus: z.number().int().min(400).max(599).optional(),
  denyMessage: z.string().max(300).optional(),
});

const zValidateSchema = z.object({
  target: z.enum(['body', 'query', 'params', 'expression']),
  expression: z.string().max(2000).optional(),
  fields: z.array(zFieldSpec),
  stripUnknown: z.boolean().optional(),
  failStatus: z.number().int().min(400).max(599).optional(),
});

const zDatasourceQuery = z.object({
  datasourceId: z.string().nullable(),
  sql: z.string().max(20000),
  params: strMap,
  single: z.boolean().optional(),
  timeoutMs: z.number().int().min(1).max(120000).optional(),
});

const zHttpRequest = z.object({
  method: zHttpMethod,
  url: z.string().max(2000),
  headers: strMap,
  query: strMap,
  bodyMode: z.enum(['none', 'json', 'text', 'form']),
  body: z.string().max(100000),
  timeoutMs: z.number().int().min(1).max(120000).optional(),
  failOnError: z.boolean().optional(),
});

const zTransformJs = z.object({ code: z.string().max(50000) });

const zTransformMap = z.object({
  source: z.string().max(2000),
  mode: z.enum(['object', 'array']),
  rules: z.array(z.object({ to: z.string().min(1).max(200), from: z.string().max(2000) })),
  keepUnmapped: z.boolean().optional(),
});

const zComparisonOp = z.enum([
  'eq', 'ne', 'gt', 'gte', 'lt', 'lte',
  'contains', 'notContains', 'startsWith', 'endsWith',
  'empty', 'notEmpty', 'truthy', 'falsy', 'in', 'notIn',
]);

const zCondition = z.object({
  left: z.string().max(2000),
  op: zComparisonOp,
  right: z.string().max(2000).optional(),
});

const zLogicCondition = z.object({
  combinator: z.enum(['and', 'or']),
  conditions: z.array(zCondition),
});

const zLogicSwitch = z.object({
  cases: z.array(z.object({ id: z.string().min(1), label: z.string().max(120), condition: zCondition })),
  hasDefault: z.boolean(),
});

const zLogicForeach = z.object({
  source: z.string().max(2000),
  itemName: z.string().min(1).max(60),
  maxIterations: z.number().int().min(1).max(10000).optional(),
  concurrency: z.number().int().min(1).max(20).optional(),
});

const zStateSet = z.object({
  assignments: z.array(z.object({ key: z.string().min(1).max(120), value: z.string().max(2000) })),
});

const zRealtimePublish = z.object({
  channel: z.string().min(1).max(120),
  payload: z.string().max(20000),
  retain: z.boolean().optional(),
});

const zUtilDelay = z.object({ ms: z.number().int().min(0).max(60000) });
const zUtilLog = z.object({ level: z.enum(['debug', 'info', 'warn', 'error']), message: z.string().max(2000) });

const zResponse = z.object({
  status: z.number().int().min(100).max(599),
  bodyMode: z.enum(['json', 'text', 'template', 'passthrough']),
  body: z.string().max(100000),
  headers: strMap,
  schema: z.array(zFieldSpec).optional(),
});

const NODE_DATA_SCHEMAS = {
  'trigger.http': zTriggerHttp,
  'auth.guard': zAuthGuard,
  'validate.schema': zValidateSchema,
  'datasource.query': zDatasourceQuery,
  'http.request': zHttpRequest,
  'transform.js': zTransformJs,
  'transform.map': zTransformMap,
  'logic.condition': zLogicCondition,
  'logic.switch': zLogicSwitch,
  'logic.foreach': zLogicForeach,
  'state.set': zStateSet,
  'util.delay': zUtilDelay,
  'util.log': zUtilLog,
  'realtime.publish': zRealtimePublish,
  response: zResponse,
} as const;

export const zNodeKind = z.enum(
  Object.keys(NODE_DATA_SCHEMAS) as [keyof typeof NODE_DATA_SCHEMAS, ...(keyof typeof NODE_DATA_SCHEMAS)[]],
);

export const zFlowNode = z
  .object({
    id: z.string().min(1).max(80),
    type: zNodeKind,
    position: z.object({ x: z.number(), y: z.number() }),
    label: z.string().max(160).optional(),
    notes: z.string().max(4000).optional(),
    disabled: z.boolean().optional(),
    data: z.record(z.string(), z.unknown()),
  })
  .superRefine((node, ctx) => {
    const schema = NODE_DATA_SCHEMAS[node.type as keyof typeof NODE_DATA_SCHEMAS];
    if (!schema) return;
    const result = schema.safeParse(node.data);
    if (!result.success) {
      for (const issue of result.error.issues) {
        ctx.addIssue({
          code: 'custom',
          path: ['data', ...issue.path],
          message: `${node.label || node.id}: ${issue.message}`,
        });
      }
    }
  });

export const zFlowEdge = z.object({
  id: z.string().min(1).max(120),
  source: z.string().min(1).max(80),
  target: z.string().min(1).max(80),
  sourceHandle: z.string().max(80).nullable().optional(),
  targetHandle: z.string().max(80).nullable().optional(),
  label: z.string().max(120).optional(),
});

const zFlowGraphShape = z
  .object({
    nodes: z.array(zFlowNode).max(300),
    edges: z.array(zFlowEdge).max(600),
    viewport: z.object({ x: z.number(), y: z.number(), zoom: z.number() }).optional(),
  })
  .superRefine((graph, ctx) => {
    const ids = new Set<string>();
    for (const n of graph.nodes) {
      if (ids.has(n.id)) {
        ctx.addIssue({ code: 'custom', message: `Duplicate node id "${n.id}"`, path: ['nodes'] });
      }
      ids.add(n.id);
    }
    for (const e of graph.edges) {
      if (!ids.has(e.source) || !ids.has(e.target)) {
        ctx.addIssue({ code: 'custom', message: `Edge "${e.id}" references a missing node`, path: ['edges'] });
      }
    }
    const triggers = graph.nodes.filter((n) => n.type === 'trigger.http');
    if (triggers.length > 1) {
      ctx.addIssue({ code: 'custom', message: 'A flow may only have one HTTP trigger', path: ['nodes'] });
    }
  });

/**
 * Node `data` is validated per-kind inside `zFlowNode.superRefine`, but zod
 * cannot express that as a discriminated union without duplicating every shape.
 * The cast re-attaches the domain type once validation has already run.
 */
export const zFlowGraph = zFlowGraphShape as unknown as z.ZodType<FlowGraph>;

/* ------------------------------- API payloads ---------------------------- */

export const zLogin = z.object({
  email: z.email().max(320),
  password: z.string().min(8).max(200),
  remember: z.boolean().optional(),
});

export const zRegister = z.object({
  email: z.email().max(320),
  password: z.string().min(8, 'Use at least 8 characters').max(200),
  name: z.string().min(1).max(120),
});

export const zProjectCreate = z.object({
  name: z.string().min(1).max(120),
  slug: z
    .string()
    .min(2)
    .max(60)
    .regex(/^[a-z0-9][a-z0-9-]*$/, 'Lowercase letters, digits and dashes only')
    .optional(),
  description: z.string().max(1000).optional(),
});

export const zProjectUpdate = z.object({
  name: z.string().min(1).max(120).optional(),
  description: z.string().max(1000).optional(),
});

export const zEndpointCreate = z.object({
  name: z.string().min(1).max(160),
  description: z.string().max(1000).optional(),
  method: zHttpMethod.optional(),
  path: z.string().max(300).optional(),
});

export const zEndpointUpdate = z.object({
  name: z.string().min(1).max(160).optional(),
  description: z.string().max(1000).optional(),
  graph: zFlowGraph.optional(),
  /** Optimistic-concurrency guard for collaborative editing. */
  baseVersion: z.number().int().nonnegative().optional(),
});

export const zDatasourceCreate = z.object({
  name: z.string().min(1).max(120),
  kind: z.enum(['postgres', 'mysql', 'sqlite', 'rest']),
  config: z.record(z.string(), zJson),
});

export const zDatasourceUpdate = z.object({
  name: z.string().min(1).max(120).optional(),
  config: z.record(z.string(), zJson).optional(),
});

/* ---------------------------- schema designer ----------------------------- */

/**
 * The designer posts structure, never SQL. Validating identifiers here means an
 * unusable name is rejected at the edge, before it reaches the DDL compiler.
 */
const zIdentifier = z
  .string()
  .min(1)
  .max(63)
  .regex(/^[A-Za-z_][A-Za-z0-9_]*$/, 'Use letters, numbers and underscores; start with a letter');

const zColumnDefault = z.union([
  z.object({ kind: z.literal('none') }),
  z.object({ kind: z.literal('literal'), value: z.string().max(200) }),
  z.object({ kind: z.literal('now') }),
]);

const zReference = z.object({ table: zIdentifier, column: zIdentifier });

export const zColumnSpec = z.object({
  name: zIdentifier,
  type: z.enum(['text', 'integer', 'real', 'boolean', 'timestamp', 'date', 'json', 'uuid']),
  nullable: z.boolean(),
  primaryKey: z.boolean(),
  unique: z.boolean(),
  autoIncrement: z.boolean().optional(),
  default: zColumnDefault.optional(),
  references: zReference.nullable().optional(),
});

export const zTableSpec = z.object({
  name: zIdentifier,
  columns: z.array(zColumnSpec).min(1).max(120),
});

export const zSchemaOp = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('createTable'), table: zTableSpec }),
  z.object({ kind: z.literal('dropTable'), table: zIdentifier }),
  z.object({ kind: z.literal('renameTable'), from: zIdentifier, to: zIdentifier }),
  z.object({ kind: z.literal('addColumn'), table: zIdentifier, column: zColumnSpec }),
  z.object({ kind: z.literal('dropColumn'), table: zIdentifier, column: zIdentifier }),
  z.object({ kind: z.literal('renameColumn'), table: zIdentifier, from: zIdentifier, to: zIdentifier }),
  z.object({ kind: z.literal('addForeignKey'), table: zIdentifier, column: zIdentifier, references: zReference }),
]);

export const zSchemaPlan = z.object({
  operations: z.array(zSchemaOp).min(1).max(200),
});

export const zWidgetFilter = z.object({
  id: z
    .string()
    .min(1)
    .max(60)
    .regex(/^[A-Za-z][A-Za-z0-9_]*$/, 'A filter id must be a valid variable name'),
  label: z.string().min(1).max(120),
  type: z.enum(['dateRange', 'select', 'text']),
  default: z.string().max(200).optional(),
  options: z
    .array(z.object({ value: z.string().max(200), label: z.string().max(200) }))
    .max(50)
    .optional(),
});

export const zWidget = z.object({
  id: z.string().min(1).max(80),
  type: z.enum(['stat', 'line', 'bar', 'area', 'pie', 'table', 'gauge', 'markdown']),
  title: z.string().max(200),
  source: z.object({
    kind: z.enum(['endpoint', 'datasource', 'static', 'stream']),
    endpointId: z.string().optional(),
    datasourceId: z.string().optional(),
    sql: z.string().max(20000).optional(),
    params: strMap.optional(),
    body: z.string().max(20000).optional(),
    staticData: zJson.optional(),
    stream: z
      .object({
        channel: z.string().min(1).max(120),
        mode: z.enum(['append', 'latest']),
        key: z.string().max(200).optional(),
        pivot: z.string().max(200).optional(),
        window: z.number().int().min(1).max(2000).optional(),
      })
      .optional(),
  }),
  mapping: z.object({
    rowsPath: z.string().max(300).optional(),
    x: z.string().max(200).optional(),
    series: z.array(z.object({ key: z.string().max(200), label: z.string().max(200).optional(), color: z.string().max(40).optional() })).optional(),
    value: z.string().max(200).optional(),
    aggregate: z.enum(['first', 'last', 'sum', 'avg', 'min', 'max', 'count']).optional(),
    columns: z.array(z.object({ key: z.string().max(200), label: z.string().max(200).optional() })).optional(),
    total: z.string().max(200).optional(),
  }),
  options: z.object({
    unit: z.string().max(40).optional(),
    precision: z.number().int().min(0).max(10).optional(),
    stacked: z.boolean().optional(),
    showLegend: z.boolean().optional(),
    showGrid: z.boolean().optional(),
    curve: z.enum(['linear', 'monotone', 'step']).optional(),
    min: z.number().optional(),
    max: z.number().optional(),
    markdown: z.string().max(20000).optional(),
    emptyText: z.string().max(300).optional(),
    pageSize: z.number().int().min(1).max(500).optional(),
    highlight: z.enum(['none', 'row', 'cell']).optional(),
  }),
  filters: z.array(zWidgetFilter).max(6).optional(),
  layout: z.object({
    x: z.number().int().min(0).max(48),
    y: z.number().int().min(0),
    w: z.number().int().min(1).max(48),
    h: z.number().int().min(1).max(48),
  }),
});

export const zDashboardCreate = z.object({
  name: z.string().min(1).max(160),
  description: z.string().max(1000).optional(),
  /** When set, widgets are generated from this endpoint's response shape. */
  fromEndpointId: z.string().optional(),
});

export const zDashboardUpdate = z.object({
  name: z.string().min(1).max(160).optional(),
  description: z.string().max(1000).optional(),
  widgets: z.array(zWidget).max(60).optional(),
  refreshMs: z.number().int().min(0).max(3600000).optional(),
  minRole: zProjectRole.optional(),
});

export const zTokenCreate = z.object({
  name: z.string().min(1).max(120),
  scopes: z.array(zTokenScope).min(1),
  role: zProjectRole,
  expiresInDays: z.number().int().min(1).max(3650).nullable().optional(),
});

export const zMemberAdd = z.object({
  email: z.email().max(320),
  role: zProjectRole,
});

export const zMemberUpdate = z.object({ role: zProjectRole });

export const zRunEndpoint = z.object({
  params: z.record(z.string(), z.string()).optional(),
  query: z.record(z.string(), z.string()).optional(),
  headers: strMap.optional(),
  body: zJson.optional(),
  /** Run the draft graph instead of the published one. */
  draft: z.boolean().optional(),
});

export type LoginInput = z.infer<typeof zLogin>;
export type RegisterInput = z.infer<typeof zRegister>;
export type WidgetInput = z.infer<typeof zWidget>;
